/**
 * Facts every legal page and the cookie banner render from. One place to edit.
 *
 * `operator.*` is required by GDPR art. 13(1)(a) and the Norwegian E-commerce Act (ehandelsloven § 8).
 * `operator` comes from @bridewell/shared so the API's email footers show the same identity.
 *
 * Bump a `*Updated` date whenever the corresponding document changes in
 * substance. Bump CONSENT_VERSION in src/lib/consent.tsx when the *categories*
 * of storage change (e.g. analytics added), so everyone is asked again.
 */
import { OPERATOR } from "@bridewell/shared";

export const LEGAL = {
  serviceName: "Bridewell",
  appHost: "bridewell.io",
  /** The checkout page's host (apps/web/worker/checkout.ts): the only place Paddle's script runs. Quoted by the policies. */
  checkoutHost: "pay.bridewell.io",
  /** The guest RSVP page's path prefix (no cookies, no storage). Quoted by the cookie policy. */
  rsvpPath: "/rsvp/",
  operator: OPERATOR,
  privacyUpdated: "2026-10-30",
  termsUpdated: "2026-11-03",
  cookiesUpdated: "2026-10-01",
  deleteAccountUpdated: "2026-10-27",
  /** Minimum age to hold an account (contractual capacity; see Terms §2). */
  minimumAge: 18,
  supervisoryAuthority: {
    name: "Datatilsynet (the Norwegian Data Protection Authority)",
    address: "Postboks 458 Sentrum, 0105 Oslo, Norway",
    url: "https://www.datatilsynet.no",
  },
  consumerAuthority: { name: "Forbrukertilsynet (the Norwegian Consumer Authority)", url: "https://www.forbrukertilsynet.no" },
  /**
   * Premium is sold through a merchant of record: Paddle is the seller on the receipt, collects
   * VAT/sales tax everywhere, runs the checkout (inside a frame on our checkout page, `checkoutHost`)
   * and handles refunds. Paddle requires this to be disclosed (Terms §4, billing page); GDPR-wise
   * Paddle is an independent controller for the payment.
   */
  merchantOfRecord: {
    short: "Paddle",
    legalName: "Paddle.com Market Limited (UK) and Paddle.com Inc. (US)",
    url: "https://www.paddle.com",
    privacyUrl: "https://www.paddle.com/legal/privacy",
    buyerTermsUrl: "https://www.paddle.com/legal/checkout-buyer-terms",
    location: "United Kingdom (EU customers) and United States (US customers); card data is processed by Paddle's PCI-DSS certified payment partners",
    safeguard: "UK adequacy decision (EU→UK); EU Standard Contractual Clauses and the EU–US Data Privacy Framework for any US processing",
  },
  /**
   * Purchases made in the Android app go through Google Play, not through Paddle: Google takes the payment,
   * handles tax, receipts and refunds under its own terms, and is an independent controller for that data.
   * Disclosed in Terms §4.8 and the Privacy Policy.
   */
  googlePlay: {
    short: "Google Play",
    url: "https://play.google.com",
    privacyUrl: "https://policies.google.com/privacy",
    termsUrl: "https://play.google.com/about/play-terms/",
  },
  /**
   * Sign in with Apple and Google: optional ways into the same account. Each provider is an independent controller of
   * the sign-in it runs; Bridewell receives only the identifier, name and email address the person chooses to share.
   */
  signInProviders: {
    apple: { short: "Apple", name: "Sign in with Apple", privacyUrl: "https://www.apple.com/legal/privacy/" },
    google: { short: "Google", name: "Sign in with Google", privacyUrl: "https://policies.google.com/privacy" },
  },
  /**
   * Purchases made in the Bridewell apps for iPhone, iPad and Mac go through Apple's App Store: Apple takes the payment,
   * handles tax, receipts and refunds under its own terms, and is an independent controller for that data. Once the
   * person signs in to a Bridewell account in those apps, the purchase is recorded with the account. Disclosed in
   * Terms §3 and §4.9 and the Privacy Policy.
   */
  appStore: {
    short: "App Store",
    url: "https://www.apple.com/app-store/",
    privacyUrl: "https://www.apple.com/legal/privacy/",
  },
  processors: [
    {
      name: "Cloudflare, Inc.",
      role: "Hosting, edge network, database (D1), file storage (R2), server logs; bot and abuse protection on every request (a small security script served from our own domain, and a security cookie only when a check is needed); a human check on the contact form (Turnstile) when enabled; optional usage statistics (Web Analytics)",
      location: "Database and files are stored in Cloudflare's Eastern Europe (EU) region. Requests are handled at the Cloudflare data centre closest to you.",
      safeguard: "EU–US Data Privacy Framework certification and EU Standard Contractual Clauses (Cloudflare Data Processing Addendum)",
      url: "https://www.cloudflare.com/privacypolicy/",
    },
    {
      name: "Resend, Inc.",
      role: "Sending account emails (verification, password reset, invitations), the reminder and digest emails you switch on, and the emails couples send their guests (guest names, addresses and the message)",
      location: "United States",
      safeguard: "EU–US Data Privacy Framework certification and EU Standard Contractual Clauses (Resend Data Processing Addendum)",
      url: "https://resend.com/legal/privacy-policy",
    },
  ],
} as const;

export function legalDate(iso: string): string {
  return new Date(`${iso}T12:00:00Z`).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
}
